import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberIdSchema } from '../identity/legacy-identity-id';

// ─── eligibility needs-review queue ─────────────────────────────────────────
//
// Wire vocabulary for the staff read that lists every member whose derived
// eligibility status is `needs_review` — the data behind the Atlas
// Eligibility Review page.
//
// The item is deliberately the smallest thing the queue can render and link
// from: which member (by Galapagos id only), which check put them there, why it
// did not match, which sponsor they named, and when. No name, date of birth,
// zip, email, access code or roster demographics: opening a row goes to the
// member page, which sits behind the member access gate, so the list itself
// never needs to reveal who a member is.

/**
 * Query params (wire shape — optional strings, clamped and decoded
 * server-side). `.strict()`: a REQUEST schema, so an unknown parameter is
 * refused rather than silently ignored (ADR-135 Rule 1).
 */
export const eligibilityReviewQueueQuerySchema = z
  .object({
    limit: z.string().optional(),
    cursor: z.string().optional(),
  })
  .strict();
export type EligibilityReviewQueueQuery = z.infer<typeof eligibilityReviewQueueQuerySchema>;

/**
 * The sponsor the member named on the check. `name` is the client-portal
 * display name when the lookup succeeds, and the ref itself otherwise, so the
 * client always has something to show.
 */
export const eligibilityReviewSponsorSchema = z.object({
  ref: z.string().min(1),
  name: z.string().min(1),
});
export type EligibilityReviewSponsor = z.infer<typeof eligibilityReviewSponsorSchema>;

/**
 * One queue row: the member's latest check. `submittedAt` is when that check
 * was submitted, which is also the member's place in line.
 *
 * Not `.strict()`, here or on the objects below: Atlas parses responses with
 * `schema.parse()`, so a strict response would fail every bundle older than a
 * deploy that adds a field (ADR-135 Rule 1).
 */
export const eligibilityReviewQueueItemSchema = z.object({
  // The shared member-id grammar, not `.uuid()`: members imported from backend2
  // keep an id with no RFC version or variant guarantee.
  memberId: memberIdSchema,
  checkId: z.string().uuid(),
  // The latest check's ledger status: `no_match`, `failed` or `matched` today
  // (the queue excludes `pending`). A string rather than an enum so a status
  // added to the ledger later cannot fail an older bundle's parse or the
  // server's own response validation (ADR-135).
  outcome: z.string().min(1),
  // A code from `ELIGIBILITY_REVIEW_REASON_CODES` or `'unrecognised'`
  // (`toStaffReviewReason`), or null. Typed as a string rather than an enum so a
  // code added later does not fail an older bundle's parse (ADR-135 Rule 1).
  reviewReason: z.string().min(1).nullable(),
  sponsor: eligibilityReviewSponsorSchema.nullable(),
  submittedAt: z.iso.datetime(),
  resolvedAt: z.iso.datetime().nullable(),
});
export type EligibilityReviewQueueItem = z.infer<typeof eligibilityReviewQueueItemSchema>;

/**
 * A keyset page plus the queue size. `nextCursor` is null on the last page.
 * `count` is the whole queue, whatever the request paged by — it feeds the
 * navigation badge.
 */
export const eligibilityReviewQueueDataSchema = z.object({
  items: z.array(eligibilityReviewQueueItemSchema),
  nextCursor: z.string().nullable(),
  count: z.number().int().nonnegative(),
});
export type EligibilityReviewQueueData = z.infer<typeof eligibilityReviewQueueDataSchema>;

export const eligibilityReviewQueueResponseSchema = apiEnvelope(eligibilityReviewQueueDataSchema);
export type EligibilityReviewQueueResponse = z.infer<typeof eligibilityReviewQueueResponseSchema>;
